import { isBrowserRenderable, resolveContentType } from '@atlas/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createClientId } from '@/lib/client-id'

/**
 * One file waiting to be classified and uploaded.
 *
 * Identified by a client-side id, not a `fileId`: nothing has been sent to the
 * server yet, so no server id exists. Everything downstream (staging overrides,
 * table rows, commit) keys off `stagedId`.
 */
export interface StagedFile {
	stagedId: string
	file: File
	fileName: string
	contentType: string
	/** Object URL for an image preview, or null for a type with no local preview. */
	previewUrl: string | null
	/** Set when this file's own upload failed during a commit; cleared on retry. */
	error: string | null
}

/**
 * Only formats the BROWSER can decode preview locally.
 *
 * Not `startsWith('image/')`: PSD, TIFF and HEIC all carry an `image/` type and
 * none of them render in an `<img>`, so that check handed the card an object
 * URL that resolved to a broken-image icon. The type marker is the honest
 * fallback until the server's own thumbnail arrives — sharp handles those
 * formats fine, which is why the preview appears after processing.
 *
 * Video first-frame capture is a separate question and not worth it pre-upload.
 */
const canPreview = (contentType: string) => isBrowserRenderable(contentType)

/**
 * The staging set for Upload & Classify: files held client-side until commit.
 *
 * Owns the object URLs so they are revoked exactly once — on removal, on
 * successful commit, and on unmount. A leaked blob URL pins the whole file in
 * memory, and this page routinely holds a batch of them.
 */
export function useStagedFiles() {
	const [staged, setStaged] = useState<StagedFile[]>([])
	// Mirrors `staged` for the unmount cleanup, which must not re-run on change.
	const stagedRef = useRef(staged)
	stagedRef.current = staged

	useEffect(() => {
		return () => {
			for (const entry of stagedRef.current) {
				if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl)
			}
		}
	}, [])

	const add = useCallback((files: File[]) => {
		const entries = files.map((file) => {
			// Resolved from the extension when the browser has no useful type —
			// Safari reports none for a `.psd`, which would otherwise show FILE on
			// the card and be rejected at commit.
			const contentType = resolveContentType(file.name, file.type)
			return {
				stagedId: createClientId('staged'),
				file,
				fileName: file.name,
				contentType,
				previewUrl: canPreview(contentType) ? URL.createObjectURL(file) : null,
				error: null,
			}
		})
		setStaged((prev) => [...prev, ...entries])
	}, [])

	const remove = useCallback((stagedId: string) => {
		setStaged((prev) => {
			const entry = prev.find((candidate) => candidate.stagedId === stagedId)
			if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl)
			return prev.filter((candidate) => candidate.stagedId !== stagedId)
		})
	}, [])

	/** Drops the files that uploaded successfully, revoking their previews. */
	const removeMany = useCallback((stagedIds: string[]) => {
		const dropped = new Set(stagedIds)
		setStaged((prev) => {
			for (const entry of prev) {
				if (dropped.has(entry.stagedId) && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl)
			}
			return prev.filter((candidate) => !dropped.has(candidate.stagedId))
		})
	}, [])

	/** Records per-file commit failures so a retry can target only what failed. */
	const setErrors = useCallback((errors: Record<string, string>) => {
		setStaged((prev) => prev.map((entry) => ({ ...entry, error: errors[entry.stagedId] ?? null })))
	}, [])

	return { staged, add, remove, removeMany, setErrors }
}
